import { CitySelectionDialog } from './components/CitySelectionDialog'
import { ColumnVisibilityButton } from './components/ColumnVisibilityButton'
import { DatePicker } from './components/DatePicker'
import { TableToolbar } from './components/TableToolbar'
import { UsersPreview } from './components/UsersPreview'
import { useTableControls, TableColumn } from './hooks/useTableControls'
import {
  getOrganizationById,
  updateOrganization as updateOrg,
  archiveOrganization as archiveOrg,
  addUserToOrganization,
  updateOrgUser,
  updateOrgPrimaryContact,
  removeUserFromOrganization,
  getAllCities as fetchAllCities,
  getAllCityGroups as fetchAllCityGroups,
  sendTestExpiryNotification,
} from './services/organizationService'
import { organizationsTheme, emotionCache } from './theme/organizationsTheme'
import { City, CityGroup, User } from './types'
import { CacheProvider } from '@emotion/react'
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import React, { useState, useMemo, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { withRouter } from 'react-router-dom'

interface Props {
  readonly history: { push: (path: string) => void }
  readonly match: { params: { id: string } }
}

const mapRoleToString = (role: User['role'] | undefined): string => {
  if (role === 'Admin') return 'admin'
  if (role === 'Private User') return 'private_user'
  return 'user'
}

/*
 * Convert a backend timestamp (ISO string) to a local YYYY-MM-DD string,
 * which is the shape an <input type="date"> accepts. Using local components
 * (not UTC) prevents the "shows yesterday" rollover when the source
 * timestamp lands on the UTC side of midnight.
 */
const toLocalDateString = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const OrganizationEdit: React.FC<Props> = ({ history, match }) => {
  const id = parseInt(match.params.id, 10)
  const intl = useIntl()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true)
  const [orgName, setOrgName] = useState('')
  const [contact, setContact] = useState('')
  const [address1, setAddress1] = useState('')
  const [address2, setAddress2] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [region, setRegion] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [orgType, setOrgType] = useState('')
  const [billingNotes, setBillingNotes] = useState('')
  const [assignedCities, setAssignedCities] = useState<number[]>([])
  const [subscriptionType, setSubscriptionType] = useState('Demo')
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [searchCityQuery, setSearchCityQuery] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [users, setUsers] = useState<User[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [saving, setSaving] = useState(false)
  const [cities, setCities] = useState<City[]>([])
  const [cityGroups, setCityGroups] = useState<CityGroup[]>([])
  const [cityDateAssignments, setCityDateAssignments] = useState<
    Record<number, string>
  >({})
  const [successSnackbar, setSuccessSnackbar] = useState('')
  const [errorSnackbar, setErrorSnackbar] = useState('')
  const [dateError, setDateError] = useState('')
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [isArchived, setIsArchived] = useState(false)

  const validateDates = (start: string, expiry: string): string => {
    if (start && expiry && new Date(start) >= new Date(expiry)) {
      return 'Expiry date must be after the start date'
    }
    return ''
  }

  const getDayAfter = (dateStr: string): string => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    date.setDate(date.getDate() + 1)
    return date.toISOString().split('T')[0]
  }

  const handleStartDateChange = (date: string) => {
    setSubscriptionStartDate(date)
    setDateError(validateDates(date, expiryDate))
    // Clear expiry if it's now invalid (on or before new start date)
    if (date && expiryDate && new Date(date) >= new Date(expiryDate)) {
      setExpiryDate('')
      setDateError('')
    }
  }

  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date)
    setDateError(validateDates(subscriptionStartDate, date))
  }

  useEffect(() => {
    fetchAllCities()
      .then((data) => setCities(data))
      .catch(() => {
        /* Ignored */
      })
    fetchAllCityGroups()
      .then((data) => setCityGroups(data))
      .catch(() => {
        /* Ignored */
      })
  }, [])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    getOrganizationById(id)
      .then((org) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!org) return
        setOrgName(org.name || '')
        setAddress1(org.address1 || '')
        setAddress2(org.address2 || '')
        setCity(org.city || '')
        setCountry(org.country || '')
        setRegion(org.province || '')
        setPostalCode(org.postal_code || '')
        setOrgType(org.org_type || '')
        setSubscriptionType(org.subscription_type || 'Professional')
        setSubscriptionStartDate(org.subscription_start || '')
        setExpiryDate(org.subscription_expiry || '')
        setBillingNotes(org.billing_notes || '')
        setAssignedCities(org.city_ids || []) // eslint-disable-line @typescript-eslint/no-unnecessary-condition
        // Build a map of city_id -> date_assigned from city_assignments
        const cityDateMap: Record<number, string> = {}
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (org.city_assignments) {
          org.city_assignments.forEach((ca) => {
            if (ca.date_assigned) {
              cityDateMap[ca.city_id] = toLocalDateString(ca.date_assigned)
            }
          })
        }
        setCityDateAssignments(cityDateMap)
        setIsArchived(Boolean(org.archived))
        const primaryId = org.primary_contact_id
          ? String(org.primary_contact_id)
          : null
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const mappedUsers: User[] = (org.users || []).map((u) => ({
          id: String(u.id),
          firstName: u.first_name || '',
          lastName: u.last_name || '',
          email: u.email || '',
          title: u.occupation || '',
          phone: u.phone || '',
          role:
            primaryId && String(u.id) === primaryId
              ? ('Primary Contact' as User['role'])
              : ('User' as User['role']),
          isPrimaryContact: primaryId ? String(u.id) === primaryId : false,
          /*
           * Normalize to local YYYY-MM-DD. The backend returns a full ISO
           * string, but the DatePicker is an <input type="date"> which only
           * accepts that shape — anything else renders as blank. Using the
           * local date (not UTC) also avoids showing "yesterday" when the
           * timestamp falls on the UTC side of midnight.
           */
          dateAssigned: toLocalDateString(u.date_assigned),
        }))
        setUsers(mappedUsers)
        if (primaryId) {
          setContact(primaryId)
        } else {
          setContact('')
        }
      })
      .catch(() => {
        /* Ignored */
      })
      .finally(() => setLoading(false))
  }, [id])

  const getSubscriptionStatus = (): { label: string; color: string } => {
    if (!expiryDate) return { label: 'Active', color: 'success' }
    const end = new Date(expiryDate)
    const now = new Date()
    const daysUntilExpiry = Math.floor(
      (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    )
    if (daysUntilExpiry < 0) return { label: 'Expired', color: 'error' }
    if (daysUntilExpiry <= 30)
      return { label: 'Expiring Soon', color: 'warning' }
    return { label: 'Active', color: 'success' }
  }

  const getNotificationDates = (): {
    thirty: string
    seven: string
    one: string
  } | null => {
    if (!expiryDate) return null
    const end = new Date(expiryDate)
    const thirtyDayNotice = new Date(end)
    thirtyDayNotice.setDate(thirtyDayNotice.getDate() - 30)
    const sevenDayNotice = new Date(end)
    sevenDayNotice.setDate(sevenDayNotice.getDate() - 7)
    const oneDayNotice = new Date(end)
    oneDayNotice.setDate(oneDayNotice.getDate() - 1)
    const formatDate = (date: Date) =>
      date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    return {
      thirty: formatDate(thirtyDayNotice),
      seven: formatDate(sevenDayNotice),
      one: formatDate(oneDayNotice),
    }
  }

  const status = getSubscriptionStatus()
  const notificationDates = getNotificationDates()

  const filteredCities = cities
    .filter((c) => {
      const isAssigned = assignedCities.includes(c.id)
      const matchesSearch =
        c.name.toLowerCase().includes(searchCityQuery.toLowerCase()) ||
        c.state.toLowerCase().includes(searchCityQuery.toLowerCase()) ||
        c.country.toLowerCase().includes(searchCityQuery.toLowerCase())
      const matchesState = !stateFilter || c.state === stateFilter
      const matchesCountry = !countryFilter || c.country === countryFilter
      return isAssigned && matchesSearch && matchesState && matchesCountry
    })
    .map((c) => ({
      ...c,
      dateAssigned: cityDateAssignments[c.id] || '',
    }))

  const cityTableColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'name',
        label: intl.formatMessage({ id: 'organizations.cityTable.name' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'state',
        label: intl.formatMessage({ id: 'organizations.cityTable.state' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'country',
        label: intl.formatMessage({ id: 'organizations.cityTable.country' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'dateAssigned',
        label: 'Date Assigned',
        visible: true,
        sortable: true,
      },
    ],
    [intl]
  )

  const {
    page: cityPage,
    rowsPerPage: cityRowsPerPage,
    columns: cityColumns,
    visibleColumns: cityVisibleColumns,
    paginatedData: paginatedCities,
    sortBy: citySortBy,
    sortDirection: citySortDirection,
    handleChangePage: handleCityChangePage,
    handleChangeRowsPerPage: handleCityChangeRowsPerPage,
    toggleColumn: toggleCityColumn,
    handleSort: handleCitySort,
  } = useTableControls(
    filteredCities,
    cityTableColumns,
    10,
    'dateAssigned',
    'desc'
  )

  const handleSave = () => {
    const error = validateDates(subscriptionStartDate, expiryDate)
    if (error) {
      setDateError(error)
      setErrorSnackbar(error)
      return
    }
    setSaving(true)
    updateOrg({
      id,
      name: orgName,
      address1,
      address2,
      city,
      province: region,
      country,
      postalCode,
      orgType,
      subscriptionType,
      subscriptionStart: subscriptionStartDate,
      subscriptionExpiry: expiryDate,
      billingNotes,
      cityIds: assignedCities,
      primaryContactId: contact ? parseInt(contact, 10) : null,
    })
      .then(() => {
        setSaving(false)
        setSuccessSnackbar('Organization saved successfully')
      })
      .catch(() => {
        setSaving(false)
        setErrorSnackbar('Failed to save organization. Please try again.')
      })
  }

  const handleArchive = () => {
    const next = !isArchived
    archiveOrg(id, next)
      .then(() => {
        setArchiveDialogOpen(false)
        if (next) {
          // archived -> drop back to the list
          history.push('/organizations')
        } else {
          // unarchived -> stay, flip the button, confirm
          setIsArchived(false)
          setSuccessSnackbar('Organization restored to circulation.')
        }
      })
      .catch(() => {
        setArchiveDialogOpen(false)
        setErrorSnackbar(
          `Failed to ${
            next ? 'archive' : 'unarchive'
          } organization. Please try again.`
        )
      })
  }

  const handleSaveCities = (selectedCities: number[]) => {
    setAssignedCities(selectedCities)
    updateOrg({
      id,
      name: orgName,
      address1,
      address2,
      city,
      province: region,
      country,
      postalCode,
      orgType,
      subscriptionType,
      subscriptionStart: subscriptionStartDate,
      subscriptionExpiry: expiryDate,
      billingNotes,
      cityIds: selectedCities,
      primaryContactId: contact ? parseInt(contact, 10) : null,
    })
      .then(() => {
        // Re-fetch org to get updated city_assignments with dates
        getOrganizationById(id).then((org) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (org?.city_assignments) {
            const cityDateMap: Record<number, string> = {}
            org.city_assignments.forEach((ca) => {
              if (ca.date_assigned) {
                cityDateMap[ca.city_id] = toLocalDateString(ca.date_assigned)
              }
            })
            setCityDateAssignments(cityDateMap)
          }
        })
      })
      .catch(() => {
        /* Ignored */
      })
  }

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={organizationsTheme}>
        <Box
          sx={{
            maxWidth: 1800,
            mx: 'auto',
            p: { xs: 2, sm: 3, md: 4 },
            pt: { xs: 10, md: 4 },
            color: '#fff',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: 4,
              gap: 2,
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => history.push('/organizations')}
                sx={{ mb: 2 }}
              >
                <FormattedMessage id="organizations.create.back" />
              </Button>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
              >
                <FormattedMessage id="organizations.edit.title" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="organizations.page.subtitle" />
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                variant="outlined"
                size="large"
                color={isArchived ? 'success' : 'error'}
                startIcon={isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                onClick={() => setArchiveDialogOpen(true)}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {isArchived ? 'Unarchive' : 'Archive'}
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <FormattedMessage id="organizations.edit.save" />
              </Button>
            </Box>
          </Box>

          {/* Organization Details */}
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                <FormattedMessage id="organizations.create.details" />
              </Typography>
            </Box>
            <Box sx={{ p: 3, overflow: 'visible' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.orgName',
                    })}
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.orgNamePlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {intl.formatMessage({
                        id: 'organizations.create.orgType',
                      })}
                    </InputLabel>
                    <Select
                      value={orgType}
                      onChange={(e) => setOrgType(e.target.value)}
                      label={intl.formatMessage({
                        id: 'organizations.create.orgType',
                      })}
                    >
                      <MenuItem value="">
                        {intl.formatMessage({
                          id: 'organizations.create.orgTypeSelect',
                        })}
                      </MenuItem>
                      <MenuItem value="Municipality">
                        {intl.formatMessage({
                          id: 'organizations.orgType.municipality',
                        })}
                      </MenuItem>
                      <MenuItem value="State Government">
                        {intl.formatMessage({
                          id: 'organizations.orgType.stateGovernment',
                        })}
                      </MenuItem>
                      <MenuItem value="Federal Government">
                        {intl.formatMessage({
                          id: 'organizations.orgType.federalGovernment',
                        })}
                      </MenuItem>
                      <MenuItem value="Non-profit/NGO">
                        {intl.formatMessage({
                          id: 'organizations.orgType.nonProfitNgo',
                        })}
                      </MenuItem>
                      <MenuItem value="Foundation">
                        {intl.formatMessage({
                          id: 'organizations.orgType.foundation',
                        })}
                      </MenuItem>
                      <MenuItem value="Private Sector">
                        {intl.formatMessage({
                          id: 'organizations.orgType.privateSector',
                        })}
                      </MenuItem>
                      <MenuItem value="Academic">
                        {intl.formatMessage({
                          id: 'organizations.orgType.academic',
                        })}
                      </MenuItem>
                      <MenuItem value="Municipal Association">
                        {intl.formatMessage({
                          id: 'organizations.orgType.municipalAssociation',
                        })}
                      </MenuItem>
                      <MenuItem value="Faith Organization">
                        {intl.formatMessage({
                          id: 'organizations.orgType.faithOrganization',
                        })}
                      </MenuItem>
                      <MenuItem value="Intergovernmental Organization">
                        {intl.formatMessage({
                          id: 'organizations.orgType.intergovernmentalOrganization',
                        })}
                      </MenuItem>
                      <MenuItem value="Other">
                        {intl.formatMessage({
                          id: 'organizations.orgType.other',
                        })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {intl.formatMessage({
                        id: 'organizations.table.contact',
                      })}
                    </InputLabel>
                    <Select
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                      label={intl.formatMessage({
                        id: 'organizations.table.contact',
                      })}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return intl.formatMessage({
                            id: 'organizations.table.contactNone',
                          })
                        }
                        const selectedUser = users.find(
                          (u) => u.id === selected
                        )
                        if (!selectedUser) {
                          return intl.formatMessage({
                            id: 'organizations.table.contactNone',
                          })
                        }
                        return `${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.email})`
                      }}
                      startAdornment={
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      }
                    >
                      <MenuItem key="empty-contact" value="" disabled>
                        {intl.formatMessage({
                          id: 'organizations.table.contactNone',
                        })}
                      </MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.address1',
                    })}
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.address1Placeholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.address2',
                    })}
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.address2Placeholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.city',
                    })}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.cityPlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.region',
                    })}
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.regionPlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.country',
                    })}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.countryPlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={intl.formatMessage({
                      id: 'organizations.create.postalCode',
                    })}
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'organizations.create.postalCodePlaceholder',
                    })}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* Subscription Details */}
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                <FormattedMessage id="organizations.create.subscription" />
              </Typography>
            </Box>
            <Box sx={{ p: 3, overflow: 'visible' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {intl.formatMessage({
                            id: 'organizations.create.subscriptionType',
                          })}
                        </InputLabel>
                        <Select
                          value={subscriptionType}
                          onChange={(e) => setSubscriptionType(e.target.value)}
                          label={intl.formatMessage({
                            id: 'organizations.create.subscriptionType',
                          })}
                        >
                          <MenuItem value="Demo">
                            {intl.formatMessage({
                              id: 'organizations.subscriptionType.demo',
                            })}
                          </MenuItem>
                          <MenuItem value="Pilot">
                            {intl.formatMessage({
                              id: 'organizations.subscriptionType.pilot',
                            })}
                          </MenuItem>
                          <MenuItem value="Custom">
                            {intl.formatMessage({
                              id: 'organizations.subscriptionType.custom',
                            })}
                          </MenuItem>
                          <MenuItem value="Canada100">
                            {intl.formatMessage({
                              id: 'organizations.subscriptionType.canada100',
                            })}
                          </MenuItem>
                          <MenuItem value="US1000">
                            {intl.formatMessage({
                              id: 'organizations.subscriptionType.us1000',
                            })}
                          </MenuItem>
                          <MenuItem value="Global1000">
                            {intl.formatMessage({
                              id: 'organizations.subscriptionType.global1000',
                            })}
                          </MenuItem>
                          <MenuItem value="Faith1000">
                            {intl.formatMessage({
                              id: 'organizations.subscriptionType.faith1000',
                            })}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        value={subscriptionStartDate}
                        onChange={handleStartDateChange}
                        placeholder={intl.formatMessage({
                          id: 'organizations.create.subscriptionStartPlaceholder',
                        })}
                        label={intl.formatMessage({
                          id: 'organizations.create.subscriptionStart',
                        })}
                        error={Boolean(dateError)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        value={expiryDate}
                        onChange={handleExpiryDateChange}
                        placeholder={intl.formatMessage({
                          id: 'organizations.create.subscriptionExpiryPlaceholder',
                        })}
                        label={intl.formatMessage({
                          id: 'organizations.create.subscriptionExpiry',
                        })}
                        error={Boolean(dateError)}
                        helperText={dateError}
                        min={getDayAfter(subscriptionStartDate)}
                      />
                    </Grid>
                    {notificationDates ? (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Notification Schedule
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                30-day notice:
                              </Typography>
                              <Typography variant="body2">
                                {notificationDates.thirty}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                7-day notice:
                              </Typography>
                              <Typography variant="body2">
                                {notificationDates.seven}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                1-day notice:
                              </Typography>
                              <Typography variant="body2">
                                {notificationDates.one}
                              </Typography>
                            </Box>
                            {process.env.REACT_APP_BACKEND_URL?.includes(
                              'localhost'
                            ) ||
                            process.env.NODE_ENV === 'development' ||
                            process.env.REACT_APP_STAGE === 'staging' ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  mt: 2,
                                  pt: 2,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ width: '100%', mb: 0.5 }}
                                >
                                  Test Notifications (Dev/Staging Only)
                                </Typography>
                                {[30, 7, 1].map((days) => (
                                  <Button
                                    key={days}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<SendIcon />}
                                    onClick={() => {
                                      sendTestExpiryNotification(id, days)
                                        .then((res) => {
                                          // eslint-disable-next-line no-alert
                                          alert(res.message)
                                        })
                                        .catch(() => {
                                          // eslint-disable-next-line no-alert
                                          alert(
                                            'Failed to send test notification'
                                          )
                                        })
                                    }}
                                    sx={{ fontSize: '0.7rem' }}
                                  >
                                    {days}-Day
                                  </Button>
                                ))}
                              </Box>
                            ) : null}
                          </Box>
                        </Paper>
                      </Grid>
                    ) : null}
                    <Grid item xs={12}>
                      <TextField
                        label={intl.formatMessage({
                          id: 'organizations.create.billingNotes',
                        })}
                        value={billingNotes}
                        onChange={(e) => setBillingNotes(e.target.value)}
                        placeholder={intl.formatMessage({
                          id: 'organizations.create.billingNotesPlaceholder',
                        })}
                        multiline
                        rows={4}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: `${status.color}.main`,
                          }}
                        />
                        <Typography variant="body2">{status.label}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Subscription Status
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        <FormattedMessage id="organizations.create.citiesAssigned" />
                      </Typography>
                      <Typography variant="h3">
                        {
                          cities.filter((c) => assignedCities.includes(c.id))
                            .length
                        }
                      </Typography>
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* Users */}
          <Box sx={{ mb: 3 }}>
            <UsersPreview
              users={users}
              setUsers={setUsers}
              onPrimaryContactChange={(email: string) => {
                const user = users.find((u) => u.email === email)
                if (user) {
                  setContact(user.id)
                  updateOrgPrimaryContact(id, parseInt(user.id, 10)).catch(
                    () => {
                      /* Ignored */
                    }
                  )
                }
              }}
              onUserAdded={(user: User) => {
                const roleStr = mapRoleToString(user.role)
                addUserToOrganization({
                  companyId: id,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                  phone: user.phone,
                  title: user.title,
                  role: roleStr,
                })
                  .then((res) => {
                    if (res.success && res.id) {
                      const newId = String(res.id)
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.id === user.id ? { ...u, id: newId } : u
                        )
                      )
                      if (user.isPrimaryContact) {
                        setContact(newId)
                        updateOrgPrimaryContact(id, parseInt(newId, 10)).catch(
                          () => {
                            /* Ignored */
                          }
                        )
                      }
                    }
                  })
                  .catch(() => {
                    /* Ignored */
                  })
              }}
              onUserUpdated={(user: Partial<User>) => {
                updateOrgUser({
                  id: parseInt(user.id ?? '0', 10),
                  firstName: user.firstName ?? '',
                  lastName: user.lastName ?? '',
                  email: user.email ?? '',
                  phone: user.phone ?? '',
                  title: user.title ?? '',
                }).catch(() => {
                  /* Ignored */
                })
              }}
              onUsersDeleted={(userIds: string[]) => {
                userIds.forEach((uid) => {
                  removeUserFromOrganization(parseInt(uid, 10)).catch(() => {
                    /* Ignored */
                  })
                })
              }}
            />
          </Box>

          {/* Cities */}
          <Card>
            <Box
              sx={{
                p: 3,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                <FormattedMessage id="organizations.create.assignedCities" />
              </Typography>
              <Button
                variant="contained"
                size="medium"
                startIcon={<EditIcon />}
                onClick={() => setIsEditModalOpen(true)}
              >
                <FormattedMessage id="organizations.create.addCities" />
              </Button>
            </Box>
            <Box
              sx={{
                p: 3,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  placeholder={intl.formatMessage({
                    id: 'organizations.searchPlaceholder',
                  })}
                  value={searchCityQuery}
                  onChange={(e) => setSearchCityQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
                <ColumnVisibilityButton
                  columns={cityColumns}
                  onToggleColumn={toggleCityColumn}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {intl.formatMessage({
                        id: 'organizations.cityTable.country',
                      })}
                    </InputLabel>
                    <Select
                      value={countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                      label={intl.formatMessage({
                        id: 'organizations.cityTable.country',
                      })}
                    >
                      <MenuItem key="all-countries" value="">
                        All Countries
                      </MenuItem>
                      {Array.from(
                        new Set(
                          cities
                            .filter((c) => assignedCities.includes(c.id))
                            .map((c) => c.country)
                        )
                      )
                        .sort((a, b) => a.localeCompare(b))
                        .map((co) => (
                          <MenuItem key={co} value={co}>
                            {co}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {intl.formatMessage({
                        id: 'organizations.cityTable.state',
                      })}
                    </InputLabel>
                    <Select
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      label={intl.formatMessage({
                        id: 'organizations.cityTable.state',
                      })}
                    >
                      <MenuItem key="all-states" value="">
                        All States
                      </MenuItem>
                      {Array.from(
                        new Set(
                          cities
                            .filter((c) => assignedCities.includes(c.id))
                            .map((c) => c.state)
                        )
                      )
                        .sort((a, b) => a.localeCompare(b))
                        .map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {cityVisibleColumns.map((column) => (
                      <TableCell key={column.id}>
                        {column.sortable !== false ? (
                          <TableSortLabel
                            active={citySortBy === column.id}
                            direction={
                              citySortBy === column.id
                                ? citySortDirection
                                : 'asc'
                            }
                            onClick={() => handleCitySort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCities.map((c) => (
                    <TableRow key={c.id} hover>
                      {cityVisibleColumns.map((column) => {
                        if (column.id === 'name')
                          return <TableCell key={column.id}>{c.name}</TableCell>
                        if (column.id === 'state')
                          return (
                            <TableCell key={column.id}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {c.state}
                              </Typography>
                            </TableCell>
                          )
                        if (column.id === 'country')
                          return (
                            <TableCell key={column.id}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {c.country}
                              </Typography>
                            </TableCell>
                          )
                        if (column.id === 'dateAssigned')
                          return (
                            <TableCell key={column.id}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {c.dateAssigned
                                  ? new Date(c.dateAssigned).toLocaleDateString(
                                      'en-US',
                                      {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      }
                                    )
                                  : '-'}
                              </Typography>
                            </TableCell>
                          )
                        return null
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableToolbar
              page={cityPage}
              rowsPerPage={cityRowsPerPage}
              totalCount={filteredCities.length}
              onChangePage={handleCityChangePage}
              onChangeRowsPerPage={handleCityChangeRowsPerPage}
            />
          </Card>

          <CitySelectionDialog
            open={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={handleSaveCities}
            cities={cities}
            cityGroups={cityGroups}
            initialSelection={assignedCities}
            title={intl.formatMessage({ id: 'organizations.create.addCities' })}
          />

          {/* Archive Confirmation Dialog */}
          <Dialog
            open={archiveDialogOpen}
            onClose={() => setArchiveDialogOpen(false)}
          >
            <DialogTitle>
              {isArchived ? 'Unarchive' : 'Archive'} Organization
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {isArchived ? (
                  <>
                    Restore <strong>{orgName}</strong> to circulation? It will
                    appear in the active organizations list again.
                  </>
                ) : (
                  <>
                    Are you sure you want to archive <strong>{orgName}</strong>?
                    This will mark the organization as archived.
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArchiveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => handleArchive()}
                color={isArchived ? 'success' : 'error'}
                variant="contained"
              >
                {isArchived ? 'Unarchive' : 'Archive'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success Snackbar */}
          <Snackbar
            open={successSnackbar !== ''}
            autoHideDuration={5000}
            onClose={() => setSuccessSnackbar('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert
              onClose={() => setSuccessSnackbar('')}
              severity="success"
              variant="filled"
            >
              {successSnackbar}
            </Alert>
          </Snackbar>

          {/* Error Snackbar */}
          <Snackbar
            open={errorSnackbar !== ''}
            autoHideDuration={5000}
            onClose={() => setErrorSnackbar('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert
              onClose={() => setErrorSnackbar('')}
              severity="error"
              variant="filled"
            >
              {errorSnackbar}
            </Alert>
          </Snackbar>
        </Box>
      </ThemeProvider>
    </CacheProvider>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default withRouter(OrganizationEdit)
